import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as ChevronDownIcon } from '../../assets/vector.svg';

const CustomSelect = ({
  options,
  value,
  onChange,
  placeholder,
  className,
  renderOption,
  getOptionLabel,
  getOptionValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = option => {
    onChange(getOptionValue(option));
    setIsOpen(false);
  };

  const getDisplayValue = () => {
    const selectedOption = options.find(opt => getOptionValue(opt) === value);

    if (!selectedOption) return placeholder;

    if (!getOptionValue(selectedOption)) {
      return placeholder;
    }

    return getOptionLabel(selectedOption);
  };

  const isPlaceholderShown = () => {
    const selectedOption = options.find(opt => getOptionValue(opt) === value);
    return !selectedOption || getOptionValue(selectedOption) === '';
  };

  return (
    <div className={`select-input ${className}`} ref={selectRef}>
      <div
        className="select-input__input"
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsOpen(!isOpen);
          }
        }}
      >
        <span className={`select-input__value ${isPlaceholderShown() ? 'placeholder' : ''}`}>
          {getDisplayValue()}
        </span>
        <span className={`select-input__arrow ${isOpen ? 'open' : ''}`}>
          <ChevronDownIcon />
        </span>
      </div>

      {isOpen && (
        <div className="select-input__dropdown">
          {options.map((option, index) => (
            <div
              key={index}
              className={`select-input__option ${
                getOptionValue(option) === value ? 'selected' : ''
              }`}
              onClick={() => handleSelect(option)}
              role="option"
              aria-selected={getOptionValue(option) === value}
            >
              {renderOption ? renderOption(option, value) : getOptionLabel(option)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

CustomSelect.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  renderOption: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
};

CustomSelect.defaultProps = {
  value: '',
  placeholder: 'Select...',
  className: '',
  renderOption: null,
  getOptionLabel: option => option.label || option,
  getOptionValue: option => (option.value !== undefined ? option.value : option),
};

export default CustomSelect;
